import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { flushSync } from 'react-dom';
import {
  Conversation,
  ConversationMessage,
  ConversationCreateRequest,
  ConversationUpdateRequest,
  MessageCreateRequest,
  ConversationSearchRequest,
  DatabaseConnection,
  MessageType,
} from '@/types';
import apiService from '@/services/api';
import { toast } from 'sonner';
import { useDatabaseConnection } from '@/contexts/DatabaseConnectionContext';

interface ConversationContextType {
  // State
  conversations: Conversation[];
  currentConversation: Conversation | null;
  messages: ConversationMessage[];
  messagesPagination: {
    total: number;
    hasMore: boolean;
    isLoadingMore: boolean;
  };
  isLoading: boolean;
  isLoadingMessages: boolean;
  selectedConnectionId: string;
  // Actions
  loadConversations: () => Promise<void>;
  createConversation: (request: ConversationCreateRequest) => Promise<Conversation | null>;
  selectConversation: (conversation: Conversation | null) => void;
  updateConversation: (id: string, request: ConversationUpdateRequest) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  loadMessages: (conversationId: string, append?: boolean) => Promise<void>;
  loadMoreMessages: () => Promise<void>;
  sendMessage: (content: string) => Promise<void>;
  processQuery: (query: string) => Promise<void>;
  searchConversations: (request: ConversationSearchRequest) => Promise<Conversation[]>;
  clearCurrentConversation: () => void;
  setMessages: React.Dispatch<React.SetStateAction<ConversationMessage[]>>;
}

const ConversationContext = createContext<ConversationContextType | undefined>(undefined);

export function ConversationProvider({ children }: { children: ReactNode }) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [messagesPagination, setMessagesPagination] = useState<{
    total: number;
    hasMore: boolean;
    isLoadingMore: boolean;
  }>({ total: 0, hasMore: false, isLoadingMore: false });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  
  // FIX: Consume selectedConnectionId from DatabaseConnectionContext instead of duplicating state
  const { selectedConnectionId } = useDatabaseConnection();
  
  const loadMessagesAbortController = useRef<AbortController | null>(null);
  const tempMessageCleanupTimer = useRef<NodeJS.Timeout | null>(null);

  // Load conversations when connection changes
  useEffect(() => {
    if (selectedConnectionId) {
      loadConversations();
    }
  }, [selectedConnectionId]);

  // Load messages when conversation changes
  useEffect(() => {
    // Cancel any pending message loads
    if (loadMessagesAbortController.current) {
      loadMessagesAbortController.current.abort();
    }

    if (currentConversation) {
      loadMessages(currentConversation.id);
    } else {
      setMessages([]);
    }
  }, [currentConversation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (loadMessagesAbortController.current) {
        loadMessagesAbortController.current.abort();
      }
      if (tempMessageCleanupTimer.current) {
        clearTimeout(tempMessageCleanupTimer.current);
      }
    };
  }, []);

  // Periodically clean up old temp and error messages
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      setMessages(prev => {
        const now = Date.now();
        return prev.filter(msg => {
          // Keep all non-temp/error/generating messages
          if (!msg.id.startsWith('temp-') && !msg.id.startsWith('error-') && !msg.id.startsWith('generating-')) {
            return true;
          }
          // Remove temp/error/generating messages older than 5 minutes
          const msgTime = new Date(msg.created_at).getTime();
          return now - msgTime < 5 * 60 * 1000;
        });
      });
    }, 60 * 1000); // Run every minute

    return () => clearInterval(cleanupInterval);
  }, []);

  const loadConversations = async () => {
    if (!selectedConnectionId) return;

    setIsLoading(true);
    try {
      const allConversations = await apiService.listConversations();
      // Filter by selected connection
      const filtered = allConversations.filter(
        conv => conv.db_connection_id === selectedConnectionId
      );
      setConversations(filtered);
    } catch (error) {
      console.error('Failed to load conversations:', error);
      toast.error('Failed to load conversations');
    } finally {
      setIsLoading(false);
    }
  };

  const createConversation = async (request: ConversationCreateRequest): Promise<Conversation | null> => {
    try {
      const conversation = await apiService.createConversation(request);
      setConversations(prev => [conversation, ...prev]);
      setCurrentConversation(conversation);
      setMessages([]);
      return conversation;
    } catch (error) {
      console.error('Failed to create conversation:', error);
      toast.error('Failed to create conversation');
      return null;
    }
  };

  const selectConversation = (conversation: Conversation | null) => {
    setCurrentConversation(conversation);
  };

  const updateConversation = async (id: string, request: ConversationUpdateRequest) => {
    try {
      const updated = await apiService.updateConversation(id, request);
      setConversations(prev =>
        prev.map(conv => (conv.id === id ? updated : conv))
      );
      if (currentConversation?.id === id) {
        setCurrentConversation(updated);
      }
    } catch (error) {
      console.error('Failed to update conversation:', error);
      toast.error('Failed to update conversation');
    }
  };

  const deleteConversation = async (id: string) => {
    try {
      await apiService.deleteConversation(id);
      setConversations(prev => prev.filter(conv => conv.id !== id));
      if (currentConversation?.id === id) {
        setCurrentConversation(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      toast.error('Failed to delete conversation');
    }
  };

  const loadMessages = async (conversationId: string, append: boolean = false) => {
    // Create new abort controller for this request
    loadMessagesAbortController.current = new AbortController();
    const currentAbortController = loadMessagesAbortController.current;

    if (append) {
      setMessagesPagination(prev => ({ ...prev, isLoadingMore: true }));
    } else {
      setIsLoadingMessages(true);
    }

    try {
      const offset = append ? messages.length : 0;
      const result = await apiService.getConversationMessages(conversationId, 50, offset);

      // Only update if this request wasn't aborted
      if (!currentAbortController.signal.aborted) {
        if (append) {
          setMessages(prev => [...prev, ...result.messages]);
        } else {
          // Preserve temp/generating/error messages during refresh
          setMessages(prev => {
            const tempMessages = prev.filter(m =>
              m.id.startsWith('temp-') ||
              m.id.startsWith('generating-') ||
              m.id.startsWith('error-')
            );
            return [...result.messages, ...tempMessages];
          });
        }
        setMessagesPagination({
          total: result.total,
          hasMore: result.has_more,
          isLoadingMore: false,
        });
      }
    } catch (error: any) {
      // Don't show error if request was aborted
      if (!currentAbortController.signal.aborted) {
        console.error('Failed to load messages:', error);
        toast.error('Failed to load messages');
      }
    } finally {
      if (!currentAbortController.signal.aborted) {
        if (append) {
          setMessagesPagination(prev => ({ ...prev, isLoadingMore: false }));
        } else {
          setIsLoadingMessages(false);
        }
      }
    }
  };

  const loadMoreMessages = async () => {
    if (!currentConversation || !messagesPagination.hasMore || messagesPagination.isLoadingMore) {
      return;
    }
    await loadMessages(currentConversation.id, true);
  };

  const sendMessage = async (content: string) => {
    if (!currentConversation) {
      toast.error('No conversation selected');
      return;
    }

    try {
      const request: MessageCreateRequest = {
        content,
        message_type: MessageType.QUERY,
      };

      const message = await apiService.addConversationMessage(
        currentConversation.id,
        request
      );

      setMessages(prev => [...prev, message]);

      // Update conversation's last message time and count
      setConversations(prev =>
        prev.map(conv =>
          conv.id === currentConversation.id
            ? {
              ...conv,
              last_message_at: message.created_at,
              message_count: conv.message_count + 1,
            }
            : conv
        )
      );
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    }
  };

  const processQuery = async (query: string) => {
    let conversationToUse = currentConversation;

    if (!conversationToUse) {
      // Create a new conversation if none exists
      const request: ConversationCreateRequest = {
        db_connection_id: selectedConnectionId,
        title: `Query: ${query.substring(0, 50)}...`,
        initial_message: query,
      };

      const newConversation = await createConversation(request);
      if (!newConversation) return;
      conversationToUse = newConversation;
    }

    try {
      const result = await apiService.processConversationQuery(
        conversationToUse.id,
        query
      );

      // Add actual messages from response
      setMessages(prev => {
        // Create a combined SQL message with query_result and explanation
        const sqlMessage = {
          ...result.sql_message,
          query_result: result.sql_message?.query_result,
          // @ts-ignore
          explanation: result.explanation,
          metadata: {
            ...result.sql_message?.metadata,
            isGenerating: false,
            _timestamp: Date.now() // Force new object reference
          }
        };

        console.log('[ConversationContext] Setting messages with SQL result:', sqlMessage.id);
        return [...prev, result.user_message, sqlMessage];
      });

      // Force loading state update to ensure re-render
      setIsLoading(false);

      // Reload conversations in background (don't block UI)
      setTimeout(() => loadConversations(), 100);
    } catch (error: any) {
      console.error('Failed to process query:', error);

      // Add error message
      setMessages(prev => {
        const errorMessage: ConversationMessage = {
          id: `error-${Date.now()}`,
          conversation_id: conversationToUse.id,
          role: 'ASSISTANT',
          message_type: MessageType.ERROR,
          content: error.message || 'Failed to process query',
          metadata: {
            original_query: query,
            isGenerating: false
          },
          created_at: new Date().toISOString(),
        };
        return [...prev, errorMessage];
      });
    }
  };

  const searchConversations = async (request: ConversationSearchRequest): Promise<Conversation[]> => {
    try {
      return await apiService.searchConversations(request);
    } catch (error) {
      console.error('Failed to search conversations:', error);
      toast.error('Failed to search conversations');
      return [];
    }
  };

  const clearCurrentConversation = () => {
    setCurrentConversation(null);
    setMessages([]);
  };

  const value: ConversationContextType = {
    conversations,
    currentConversation,
    messages,
    messagesPagination,
    isLoading,
    isLoadingMessages,
    selectedConnectionId,
    loadConversations,
    createConversation,
    selectConversation,
    updateConversation,
    deleteConversation,
    loadMessages,
    loadMoreMessages,
    sendMessage,
    processQuery,
    searchConversations,
    clearCurrentConversation,
    setMessages,
  };

  return (
    <ConversationContext.Provider value={value}>
      {children}
    </ConversationContext.Provider>
  );
}

export function useConversation() {
  const context = useContext(ConversationContext);
  if (context === undefined) {
    throw new Error('useConversation must be used within a ConversationProvider');
  }
  return context;
}